import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';
import { setCookie } from '../../../utils/setCookie';
import { createApiClient } from '../../apiClient';
import { emitEvent } from '../../clientEvents';
import { fetchProjectSettings } from '../../projectSettings/fetchProjectSettings';
import { isCookieEnabled } from '../../projectSettings/isCookieEnabled';
import { generateSessionKeys } from '../../sessionKeys/generateSessionKeys';
import { disconnectAndTerminateWalletProviders } from '../../wallets/disconnectAndTerminateWalletProviders';
import { DYNAMIC_AUTH_COOKIE_NAME } from '../consts';

/**
 * Logs out the current user and clears all authentication data.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves when the logout process is complete.
 */
export const logout = async (client = getDefaultClient()) => {
  const core = getCore(client);

  core.logger.debug('[logout] Logging out...');

  await disconnectAndTerminateWalletProviders(client);

  if (client.user !== null) {
    const apiClient = createApiClient({}, client);

    try {
      await apiClient.revokeSession({
        environmentId: core.environmentId,
      });
    } catch (error) {
      core.logger.error('Failed to revoke session', error);
    }

    /**
     * This deletes the auth cookie if it exists.
     * If the cookie doesn't exist, this sets a new cookie that expires immediately.
     */
    if (isCookieEnabled(client)) {
      setCookie(
        `${DYNAMIC_AUTH_COOKIE_NAME}=; Max-Age=-99999999; path=/; SameSite=Lax`
      );
    }
  }

  core.state.set({
    captchaToken: null,
    elevatedAccessTokens: [],
    legacyToken: null,
    mfaToken: null,
    sessionExpiresAt: null,
    sessionKeys: null,
    token: null,
    unverifiedWalletAccounts: [],
    user: null,
  });

  emitEvent({ event: 'logout' }, client);

  // Refetch project settings
  void fetchProjectSettings(client);

  // Regenerate session keys
  void generateSessionKeys(client);
};
